import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Menu, MenuProps } from "antd";
import Sider from "antd/es/layout/Sider";
import { isMobile } from "react-device-detect";
import { PanelLeft, PanelLeftClose } from "lucide-react";

import Changelog from "components/changelog/changelog";
import SidebarFooter from "components/sidebar-footer/sidebar-footer";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useNavProfile } from "hooks/useNavProfile";

import { useBrand } from "context/BrandContext";
import routeNames from "constants/route-names";

type SiderProps = {
  isCollapsed: boolean;
  menuItems: MenuProps["items"];
  selectedKey: string;
  selectedSubmenu?: string;
  isAdminPage?: boolean;
  handleOnCollapse: (collapsed: boolean) => void;
  toggle: () => void;
  handleClose: () => void;
  handleOnClickLogo: () => void;
  onMenuItemClick?: MenuProps["onClick"];
};

const AppSider = ({
  isCollapsed,
  menuItems,
  selectedKey,
  selectedSubmenu = '',
  isAdminPage,
  handleOnCollapse,
  toggle,
  handleClose,
  handleOnClickLogo,
  onMenuItemClick,
}: SiderProps) => {
  const { theme } = useBrand();
  const { navProfile } = useNavProfile();
  const { loggedUserValue } = useLoggedUser();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { guideId } = useParams<{ guideId: string }>();
  const [signupUrl, setSignupUrl] = useState(routeNames.SIGNUP);
  const isPathHandovers = pathname.includes('handovers')
  const isOllieBrand = navProfile.sidebarBrand === "ollie";
  const showToggle = !!loggedUserValue?.id || !isPathHandovers;

  useEffect(() => {
    if (guideId) {
      setSignupUrl(`${routeNames.SIGNUP}?guideId=${guideId}`);
    }
  }, [guideId]);

  return (
    <>
      <Sider
        breakpoint="lg"
        width={navProfile.sidebarWidth}
        collapsedWidth={isMobile ? 0 : navProfile.sidebarCollapsedWidth}
        className="sidebar"
        collapsible
        collapsed={isCollapsed}
        onCollapse={handleOnCollapse}
        trigger={null}
      >
        <div className="sidebar-content">
          <div>
            {/* {isMobile && (
              <img
                className="sidebar-close-icon"
                src={`${process.env.PUBLIC_URL}/assets/images/icons/x.svg`}
                onClick={handleClose}
              />
            )} */}
            <div className="sidebar-header sidebar-header-left-aligned">
              <div className="admin-sidebar-logo-container">
                <div className="sidebar-logo-container" onClick={handleOnClickLogo}>
                  {isOllieBrand ? (
                    <div className="sidebar-ollie-brand">
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`}
                        className="sidebar-ollie-brand-owl"
                        alt="Ollie"
                      />
                      {!isCollapsed && <span className="sidebar-ollie-brand-name">Ollie.</span>}
                    </div>
                  ) : (
                    <object
                      type="image/svg+xml"
                      data={`${process.env.PUBLIC_URL}${theme.navbarImage}`}
                      className="sidebar-logo"
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}${theme.navbarImage}`}
                        className="sidebar-logo"
                      />
                    </object>
                  )}
                </div>
                {isAdminPage && <span className="admin-badge">ADMIN</span>}
              </div>

              {showToggle && (
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={toggle}
                  aria-label={isCollapsed ? "Expand side navigation" : "Collapse side navigation"}
                >
                  {isCollapsed ? <PanelLeft size={24} strokeWidth={1.22} /> : <PanelLeftClose size={24} strokeWidth={1.33} />}
                </button>
              )}
            </div>

            <Menu
              className="sidebar-menu"
              mode="inline"
              selectedKeys={[selectedKey]}
              openKeys={[selectedSubmenu]}
              items={menuItems}
              onClick={onMenuItemClick}
            />
          </div>

          {loggedUserValue?.id && (
            <div className="changelog-section">
              <Changelog />
            </div>
          )}

          {loggedUserValue?.id && <SidebarFooter isCollapsed={isCollapsed} />}

          {(!isPathHandovers && !loggedUserValue?.id) && (
            <div className="bottom-nav-bar">
              <button
                className="admin-toggle-button"
                onClick={() => navigate(signupUrl)}
              >
                <span>Create account</span>
              </button>
            </div>
          )}
        </div>
      </Sider>
      {!isCollapsed && <div className="sidebar-backdrop" onClick={handleClose} />}
    </>
  );
};

export default AppSider;

// was commented out on logged navigation layout. Keeping just in case it comes back
{
  /* {loggedUserValue.selectedProperty?.premium ? null : ( //TODO: THIS FEATURE WILL COME BACK LATER
              <div className="sidebar-chat-link">
                <div className="sidebar-chat-mask"></div>
                <div className="sidebar-chat-content">
                  <>
                    <span className="sidebar-chat-title">{"Need help?"}</span>
                    <span className="sidebar-chat-subtitle">
                      {"Upgrade to premium"}
                    </span>
                    <button
                      className="sidebar-chat-button"
                      // onClick={handleOnClickExpand}
                    >
                      UPGRADE NOW
                    </button>
                  </>
                </div>
              </div>
            )} */
}
